import { useParams, Link } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';
import { useMemo, useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredComics = useMemo(() => {
    if (!category) return [];
    let result = comics.filter((c) => c.category === category.slug);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [category, sortBy]);

  if (!category) {
    return (
      <main className="px-4 md:px-6 py-20 text-center">
        <p className="text-foreground-600">分类不存在</p>
        <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block whitespace-nowrap cursor-pointer">返回首页</Link>
      </main>
    );
  }

  return (
    <main>
      <article className="px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-[1280px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{category.name}</span>
          </nav>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
              <p className="text-sm text-foreground-600 mt-1">共 {category.count} 部漫画作品</p>
            </div>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: 'popular', label: '人气' },
                { key: 'latest', label: '最新' },
                { key: 'rating', label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${category.name}漫画`}
                    title={`${comic.title} - 女神漫画网${category.name}免费阅读`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      comic.status === '连载中'
                        ? 'bg-accent-500/90 text-background-50'
                        : 'bg-foreground-500/80 text-background-50'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author} · 共{comic.chapters}话</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-400 text-xs"></i>
                      <span className="text-xs text-foreground-600">{comic.rating}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{Math.floor(comic.views / 10000)}万阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </article>
    </main>
  );
}